import { z } from 'zod';

/**
 * Typed, runtime-validated environment. All client config comes from
 * `import.meta.env.VITE_*` — never hardcoded. Import `env` everywhere instead
 * of touching `import.meta.env` directly.
 *
 * The app speaks the real backend contract (see docs/api/endpoints.json). Two run
 * modes, switched entirely by env — no code change:
 *  - **Mock** (default in dev): MSW serves the whole API in-browser. `apiUrl` is
 *    empty so requests are same-origin and the worker intercepts them.
 *  - **Live**: point `VITE_API_URL` at the running backend (e.g.
 *    `http://localhost:8080`) and set `VITE_ENABLE_MOCKS=false`. LiveKit rooms
 *    are then minted by the backend and joined for real.
 */
/**
 * The same tier vocabulary the three backend services use, so one value means one
 * thing across the whole system. `dev` sits with staging and production because
 * the deployed dev environment is a real deployment that merely shares a laptop's
 * name.
 */
const DEV_ENVIRONMENTS = ['ci', 'development', 'docker', 'local', 'test', 'testing'] as const;
const DEPLOYED_ENVIRONMENTS = ['dev', 'staging', 'production'] as const;

const envSchema = z.object({
  // Empty = same-origin (mock mode). Live: e.g. "http://localhost:8080".
  VITE_API_URL: z.string().default(''),
  // Kept optional so "unset" stays distinct from "false" — the default (on in
  // dev) is resolved below.
  VITE_ENABLE_MOCKS: z.enum(['true', 'false']).optional(),
  // Which tier this bundle was built for. Optional so a local checkout needs no
  // extra setup; a deployed build sets it and gets the guard below.
  VITE_APP_ENV: z.enum([...DEV_ENVIRONMENTS, ...DEPLOYED_ENVIRONMENTS]).optional(),
  // How the delivered prototype is framed. `inline` puts the markup in `srcdoc`, where
  // the frame INHERITS this app's `script-src 'self'` and the partner's own inline
  // scripts are blocked — it draws but does not click. `url` points the frame at the
  // API's viewer, whose response carries a policy written for exactly this, so the
  // scripts run inside an opaque sandbox origin.
  //
  // It is configuration and not a guess because the deciding fact lives in the edge
  // policy, which the bundle cannot read: `url` needs `frame-src` to name the API
  // origin, and a frame refused by CSP still fires `load`, so there is nothing
  // trustworthy to detect at runtime. Default `inline` keeps every tier exactly as it
  // is until its edge is ready.
  VITE_PROTOTYPE_FRAME_MODE: z.enum(['inline', 'url']).optional(),
  // Where a deployed MCP server answers, for the setup instructions the connect page
  // hands out. Configuration rather than derivation: the SPA and the API sit on
  // different hosts in every deployed tier, so neither this page's origin nor the API's
  // is the MCP's, and a wrong URL in a copied config block is a support ticket.
  VITE_MCP_URL: z.string().default(''),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  // Surfaces misconfiguration loudly at startup rather than as obscure runtime bugs.
  console.error('Invalid environment configuration:', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid environment configuration. Check your .env.local against .env.example.');
}

const raw = parsed.data;

const appEnv = raw.VITE_APP_ENV;
const isDeployedEnvironment =
  appEnv !== undefined && (DEPLOYED_ENVIRONMENTS as readonly string[]).includes(appEnv);
const enableMocks =
  raw.VITE_ENABLE_MOCKS === undefined ? import.meta.env.DEV : raw.VITE_ENABLE_MOCKS === 'true';

if (isDeployedEnvironment && enableMocks) {
  // A deployed bundle serving MSW fixtures looks completely healthy — pages render,
  // lists fill, nothing errors — while touching no backend at all. That is far worse
  // than a blank screen, so it fails loudly at startup instead.
  throw new Error(
    `VITE_ENABLE_MOCKS is on in the "${appEnv}" environment. A deployed build must talk ` +
      'to the real API; set VITE_ENABLE_MOCKS=false.',
  );
}

const apiUrl = raw.VITE_API_URL.replace(/\/+$/, '');

/** The API root the MCP client wants: the same base WITHOUT the version prefix, because
 *  `DDA_API_URL` is documented as the root and the client appends `/api/v1` itself.
 *
 *  `VITE_API_URL` is the inverse — it INCLUDES the prefix, since axios calls paths under
 *  it. Handing the browser value straight to an MCP client produced `/api/v1/api/v1/...`
 *  and a 404 on every call, in a config block that told the reader on the next line that
 *  the value must not include the prefix. */
const apiRoot = apiUrl.replace(/\/api\/v\d+$/, '');

/** Where a deployed MCP server answers.
 *
 *  Derived from the API's origin rather than this page's: the SPA and the API sit on
 *  different hosts in every deployed tier, but the MCP is published beside the API under
 *  the same one. `VITE_MCP_URL` overrides it for a tier that puts it somewhere else.
 *
 *  Derived rather than left empty because an unset variable was not a neutral default —
 *  it made the page tell people "this environment publishes no HTTP endpoint" while one
 *  was live and serving, and send them to the transport they have to install. */
function mcpEndpoint(): string {
  const configured = raw.VITE_MCP_URL.replace(/\/+$/, '');
  if (configured) return configured;
  try {
    return `${new URL(apiRoot).origin}/dda-mcp/api/v1/mcp`;
  } catch {
    // A relative apiUrl — mock mode, or a same-origin dev proxy. There is no deployed
    // MCP to name in either, and guessing one would be worse than offering stdio.
    return '';
  }
}

export const env = {
  /** Backend base URL, prefix included. Empty string = same-origin (mock mode). */
  apiUrl,
  /** The same base without the version prefix — what an MCP client's DDA_API_URL wants. */
  apiRoot,
  /** Mocks default ON in dev, OFF in prod, unless explicitly overridden. */
  enableMocks,
  /** Which tier this bundle targets, when the build declared one. */
  appEnv,
  /** True for dev/staging/production — a real deployment, whatever its name. */
  isDeployedEnvironment,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  /** How to frame a delivered prototype. See `VITE_PROTOTYPE_FRAME_MODE` above. */
  prototypeFrameMode: raw.VITE_PROTOTYPE_FRAME_MODE ?? 'inline',
  /** The deployed MCP endpoint, or '' when this build cannot name one. */
  mcpUrl: mcpEndpoint(),
} as const;

export type Env = typeof env;
